import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../home/components/Header';
import Footer from '../home/components/Footer';
import { usePageMeta } from '../../hooks/usePageMeta';

export default function PrivacyPage() {
  // 設定隱私權政策頁 Meta
  usePageMeta({
    title: '隱私權政策 | laigame手遊代儲',
    description: 'laigame手遊代儲隱私權政策，詳細說明個人資料蒐集範圍、使用方式、保護措施、Cookie使用、您的權利等重要資訊，我們重視您的隱私權保護。',
    keywords: '隱私權政策,個人資料保護,資料安全,Cookie政策,會員權利,laigame',
    ogTitle: '隱私權政策 | laigame手遊代儲',
    ogDescription: 'laigame手遊代儲隱私權政策，詳細說明個人資料蒐集範圍、使用方式、保護措施等重要資訊'
  });

  useEffect(() => {
    const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://example.com';

    const webpageSchema = {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "@id": `${SITE_URL}/privacy#webpage`,
      "name": "隱私權政策 - laigame手遊代儲",
      "description": "laigame手遊代儲隱私權政策，說明我們如何蒐集、使用、保護您的個人資料",
      "url": `${SITE_URL}/privacy`,
      "inLanguage": "zh-TW",
      "isPartOf": {
        "@type": "WebSite",
        "@id": `${SITE_URL}/#website`,
        "name": "laigame手遊代儲",
        "url": SITE_URL
      }
    };

    const breadcrumbSchema = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "@id": `${SITE_URL}/privacy#breadcrumb`,
      "itemListElement": [
        {
          "@type": "ListItem",
          "position": 1,
          "name": "首頁",
          "item": `${SITE_URL}/`
        },
        {
          "@type": "ListItem",
          "position": 2,
          "name": "隱私權政策",
          "item": `${SITE_URL}/privacy`
        }
      ]
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'privacy-schema';
    script.text = JSON.stringify(webpageSchema);
    document.head.appendChild(script);

    const breadcrumbScript = document.createElement('script');
    breadcrumbScript.type = 'application/ld+json';
    breadcrumbScript.id = 'privacy-breadcrumb-schema';
    breadcrumbScript.text = JSON.stringify(breadcrumbSchema);
    document.head.appendChild(breadcrumbScript);

    return () => {
      ['privacy-schema', 'privacy-breadcrumb-schema'].forEach(sid => {
        const el = document.getElementById(sid);
        if (el) el.remove();
      });
    };
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-purple-50 to-pink-50">
      <Header />
      
      {/* Hero Banner */}
      <div className="relative bg-gradient-to-r from-purple-600 via-pink-600 to-rose-600 text-white py-16 overflow-hidden">
        <div className="absolute inset-0 bg-black/10"></div>
        <div className="absolute top-0 right-0 w-96 h-96 bg-white/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-rose-500/20 rounded-full blur-3xl"></div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex items-center space-x-2 text-sm mb-6">
            <Link to="/" className="hover:text-purple-200 transition-colors">首頁</Link>
            <i className="ri-arrow-right-s-line text-purple-200"></i>
            <span className="text-purple-100">隱私權政策</span>
          </nav>
          
          <h1 className="text-4xl md:text-5xl font-bold mb-4">隱私權政策</h1>
          <p className="text-lg text-purple-100 max-w-2xl">
            我們重視您的隱私權，本政策說明我們如何蒐集、使用、保護您的個人資料。
          </p>
          <div className="mt-6 text-sm text-purple-100">
            最後更新日期：2024 年 1 月
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-white rounded-2xl shadow-lg p-8 md:p-12">
          
          {/* Introduction */}
          <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-2xl p-6 mb-12">
            <div className="flex items-start">
              <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-600 rounded-xl flex items-center justify-center mr-4 flex-shrink-0">
                <i className="ri-shield-user-line text-white text-xl"></i>
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">我們的承諾</h3>
                <p className="text-gray-700 text-sm leading-relaxed">
                  本網站（以下簡稱「本站」）非常重視您的隱私權保護，並遵守個人資料保護法及相關法令規定。本隱私權政策說明本站如何蒐集、處理及利用您的個人資料，以及您所享有的權利。
                </p>
              </div>
            </div>
          </div>

          {/* Section 1 */}
          <section className="mb-12">
            <div className="flex items-center mb-6">
              <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-cyan-600 rounded-xl flex items-center justify-center mr-4">
                <i className="ri-database-2-line text-white text-xl"></i>
              </div>
              <h2 className="text-2xl font-bold text-gray-900">一、個人資料蒐集範圍與目的</h2>
            </div>
            <div className="pl-16 space-y-4 text-gray-700 leading-relaxed">
              <p>1.1 <strong>蒐集的個人資料類型：</strong></p>
              <ul className="list-disc list-inside pl-4 space-y-2">
                <li>識別資料：姓名、電子郵件、手機號碼</li>
                <li>帳號資料：會員帳號、密碼（加密儲存）</li>
                <li>交易資料：訂單記錄、付款資訊、購物金餘額</li>
                <li>遊戲資料：遊戲帳號、伺服器選擇（僅用於代儲服務，不儲存密碼）</li>
                <li>推薦資料：專屬推薦代碼、推薦關係</li>
                <li>銀行資料：銀行帳號（僅限申請現金回饋者）</li>
                <li>技術資料：IP 位址、瀏覽器類型、裝置資訊、Cookie</li>
              </ul>
              
              <p className="mt-6">1.2 <strong>蒐集目的：</strong></p>
              <ul className="list-disc list-inside pl-4 space-y-2">
                <li>提供手遊代儲、點數卡販售等服務</li>
                <li>處理訂單、付款及客戶服務</li>
                <li>會員身份驗證與帳號管理</li>
                <li>代理回饋制度之計算與發放</li>
                <li>購物金與現金回饋之管理</li>
                <li>網站功能優化與使用者體驗改善</li>
                <li>行銷推廣與優惠通知（需經您同意）</li>
                <li>法律義務履行與爭議處理</li>
              </ul>

              <p className="mt-6">1.3 <strong>蒐集方式：</strong></p>
              <ul className="list-disc list-inside pl-4 space-y-2">
                <li>您主動提供：註冊會員、下單購買、填寫表單時</li>
                <li>自動蒐集：使用網站時透過 Cookie 等技術自動記錄</li>
                <li>第三方來源：金流服務商提供的交易資訊</li>
              </ul>
            </div>
          </section>

          {/* Section 2 */}
          <section className="mb-12">
            <div className="flex items-center mb-6">
              <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl flex items-center justify-center mr-4">
                <i className="ri-file-list-3-line text-white text-xl"></i>
              </div>
              <h2 className="text-2xl font-bold text-gray-900">二、個人資料使用方式</h2>
            </div>
            <div className="pl-16 space-y-4 text-gray-700 leading-relaxed">
              <p>2.1 <strong>服務提供：</strong></p>
              <ul className="list-disc list-inside pl-4 space-y-2">
                <li>處理您的訂單並完成代儲或點卡發送</li>
                <li>發送訂單確認、付款通知、儲值完成等簡訊或電子郵件</li>
                <li>提供客戶服務與技術支援</li>
                <li>處理退款申請與爭議解決</li>
              </ul>
              
              <p className="mt-6">2.2 <strong>代理回饋制度：</strong></p>
              <ul className="list-disc list-inside pl-4 space-y-2">
                <li>追蹤推薦關係與新會員註冊</li>
                <li>計算推薦消費回饋金額（1%）</li>
                <li>發放購物金或處理現金提領</li>
                <li>驗證銀行帳戶資訊（僅限現金回饋）</li>
              </ul>

              <p className="mt-6">2.3 <strong>網站優化：</strong></p>
              <ul className="list-disc list-inside pl-4 space-y-2">
                <li>分析網站使用情況，改善使用者體驗</li>
                <li>偵測並防止詐欺、濫用或違法行為</li>
                <li>維護網站安全與系統穩定</li>
              </ul>

              <p className="mt-6">2.4 <strong>行銷推廣（需經您同意）：</strong></p>
              <ul className="list-disc list-inside pl-4 space-y-2">
                <li>發送優惠活動、新品上架等行銷訊息</li>
                <li>提供個人化推薦與優惠</li>
                <li>您可隨時取消訂閱行銷訊息</li>
              </ul>
            </div>
          </section>

          {/* Section 3 */}
          <section className="mb-12">
            <div className="flex items-center mb-6">
              <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-600 rounded-xl flex items-center justify-center mr-4">
                <i className="ri-shield-check-line text-white text-xl"></i>
              </div>
              <h2 className="text-2xl font-bold text-gray-900">三、個人資料保護措施</h2>
            </div>
            <div className="pl-16 space-y-4 text-gray-700 leading-relaxed">
              <p>3.1 <strong>技術安全措施：</strong></p>
              <ul className="list-disc list-inside pl-4 space-y-2">
                <li>使用 SSL/TLS 加密技術保護資料傳輸</li>
                <li>密碼採用加密演算法儲存，本站無法得知您的明碼密碼</li>
                <li>定期進行系統安全檢測與漏洞修補</li>
                <li>設置防火牆與入侵偵測系統</li>
                <li>遊戲帳號密碼不儲存於本站系統</li>
              </ul>
              
              <p className="mt-6">3.2 <strong>管理安全措施：</strong></p>
              <ul className="list-disc list-inside pl-4 space-y-2">
                <li>限制員工存取權限，僅授權人員可接觸個人資料</li>
                <li>員工簽署保密協議並接受資安教育訓練</li>
                <li>定期備份資料並建立災難復原機制</li>
                <li>記錄資料存取日誌，以便追蹤異常行為</li>
              </ul>

              <p className="mt-6">3.3 <strong>第三方安全：</strong></p>
              <ul className="list-disc list-inside pl-4 space-y-2">
                <li>金流服務商均符合 PCI DSS 等國際安全標準</li>
                <li>與第三方簽署資料保護協議</li>
                <li>定期評估第三方服務商的資安能力</li>
              </ul>

              <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded-r-lg mt-4">
                <div className="flex items-start">
                  <i className="ri-information-line text-blue-600 text-xl mr-3 mt-0.5"></i>
                  <div>
                    <p className="font-semibold text-blue-900 mb-1">重要提醒</p>
                    <p className="text-blue-800 text-sm">儘管我們採取多重安全措施，網路傳輸仍存在風險。請妥善保管您的帳號密碼，不要與他人分享，並定期更換密碼。</p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Section 4 */}
          <section className="mb-12">
            <div className="flex items-center mb-6">
              <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-red-600 rounded-xl flex items-center justify-center mr-4">
                <i className="ri-cookie-line text-white text-xl"></i>
              </div>
              <h2 className="text-2xl font-bold text-gray-900">四、Cookie 使用說明</h2>
            </div>
            <div className="pl-16 space-y-4 text-gray-700 leading-relaxed">
              <p>4.1 <strong>什麼是 Cookie：</strong></p>
              <p>Cookie 是網站儲存在您瀏覽器中的小型文字檔案，用於記錄您的偏好設定與使用行為。</p>
              
              <p className="mt-6">4.2 <strong>我們使用的 Cookie 類型：</strong></p>
              <ul className="list-disc list-inside pl-4 space-y-2">
                <li><strong>必要性 Cookie：</strong>維持網站基本功能，如登入狀態、購物車內容</li>
                <li><strong>功能性 Cookie：</strong>記住您的偏好設定，如語言選擇</li>
                <li><strong>分析性 Cookie：</strong>了解網站使用情況，改善使用者體驗</li>
                <li><strong>行銷性 Cookie：</strong>提供個人化廣告與推薦（需經您同意）</li>
              </ul>

              <p className="mt-6">4.3 <strong>如何管理 Cookie：</strong></p>
              <p>您可以透過瀏覽器設定管理或刪除 Cookie。請注意，停用必要性 Cookie 可能影響網站功能正常運作。</p>
              
              <p className="mt-4">主流瀏覽器 Cookie 設定方式：</p>
              <ul className="list-disc list-inside pl-4 space-y-2">
                <li>Chrome：設定 → 隱私權和安全性 → Cookie 和其他網站資料</li>
                <li>Safari：偏好設定 → 隱私權 → Cookie 和網站資料</li>
                <li>Firefox：選項 → 隱私權與安全性 → Cookie 與網站資料</li>
                <li>Edge：設定 → Cookie 和網站權限</li>
              </ul>
            </div>
          </section>

          {/* Section 5 */}
          <section className="mb-12">
            <div className="flex items-center mb-6">
              <div className="w-12 h-12 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl flex items-center justify-center mr-4">
                <i className="ri-links-line text-white text-xl"></i>
              </div>
              <h2 className="text-2xl font-bold text-gray-900">五、第三方服務說明</h2>
            </div>
            <div className="pl-16 space-y-4 text-gray-700 leading-relaxed">
              <p>5.1 <strong>金流服務商：</strong></p>
              <p>本站使用第三方金流服務處理付款，包括 ATM 轉帳、超商代碼等。您的付款資訊將依照金流服務商的隱私權政策處理，本站不儲存您的信用卡號或銀行帳戶完整資訊。</p>
              
              <p className="mt-6">5.2 <strong>簡訊服務商：</strong></p>
              <p>本站使用第三方簡訊服務發送訂單通知、驗證碼等訊息。您的手機號碼將依照簡訊服務商的隱私權政策處理。</p>

              <p className="mt-6">5.3 <strong>遊戲官方平台：</strong></p>
              <p>代儲服務需透過遊戲官方平台完成，您的遊戲帳號資訊將依照遊戲官方的隱私權政策處理。本站不儲存您的遊戲密碼。</p>

              <p className="mt-6">5.4 <strong>外部連結：</strong></p>
              <p>本站可能包含連結至其他網站的超連結。這些外部網站有各自的隱私權政策，本站不對其內容或隱私保護措施負責。</p>

              <div className="bg-amber-50 border-l-4 border-amber-500 p-4 rounded-r-lg mt-4">
                <div className="flex items-start">
                  <i className="ri-alert-line text-amber-600 text-xl mr-3 mt-0.5"></i>
                  <div>
                    <p className="font-semibold text-amber-900 mb-1">注意事項</p>
                    <p className="text-amber-800 text-sm">點擊外部連結前，請先閱讀該網站的隱私權政策。本站無法控制第三方網站如何使用您的個人資料。</p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Section 6 */}
          <section className="mb-12">
            <div className="flex items-center mb-6">
              <div className="w-12 h-12 bg-gradient-to-br from-teal-500 to-cyan-600 rounded-xl flex items-center justify-center mr-4">
                <i className="ri-user-settings-line text-white text-xl"></i>
              </div>
              <h2 className="text-2xl font-bold text-gray-900">六、您的權利</h2>
            </div>
            <div className="pl-16 space-y-4 text-gray-700 leading-relaxed">
              <p>依據個人資料保護法，您對自己的個人資料享有以下權利：</p>
              
              <p className="mt-4">6.1 <strong>查詢或請求閱覽：</strong>您可以要求查閱本站持有的您的個人資料。</p>
              
              <p className="mt-4">6.2 <strong>請求製給複製本：</strong>您可以要求本站提供您的個人資料副本。</p>
              
              <p className="mt-4">6.3 <strong>請求補充或更正：</strong>若您的個人資料有錯誤或不完整，您可以要求更正或補充。</p>
              
              <p className="mt-4">6.4 <strong>請求停止蒐集、處理或利用：</strong>在特定情況下，您可以要求本站停止使用您的個人資料。</p>
              
              <p className="mt-4">6.5 <strong>請求刪除：</strong>在特定情況下，您可以要求本站刪除您的個人資料。</p>

              <p className="mt-6"><strong>如何行使您的權利：</strong></p>
              <ul className="list-disc list-inside pl-4 space-y-2">
                <li>登入會員中心自行修改部分資料</li>
                <li>透過 LINE 或 Facebook 聯繫客服提出申請</li>
                <li>本站將在收到申請後 15 個工作天內回覆</li>
              </ul>

              <p className="mt-6"><strong>權利限制：</strong></p>
              <p>在以下情況下，本站可能無法完全滿足您的請求：</p>
              <ul className="list-disc list-inside pl-4 space-y-2">
                <li>法律要求本站保留特定資料</li>
                <li>資料刪除將影響進行中的交易或服務</li>
                <li>資料用於防止詐欺或維護系統安全</li>
                <li>資料已匿名化處理，無法識別個人</li>
              </ul>
            </div>
          </section>

          {/* Section 7 */}
          <section className="mb-12">
            <div className="flex items-center mb-6">
              <div className="w-12 h-12 bg-gradient-to-br from-rose-500 to-pink-600 rounded-xl flex items-center justify-center mr-4">
                <i className="ri-time-line text-white text-xl"></i>
              </div>
              <h2 className="text-2xl font-bold text-gray-900">七、資料保存期限</h2>
            </div>
            <div className="pl-16 space-y-4 text-gray-700 leading-relaxed">
              <p>7.1 <strong>會員資料：</strong>保存至會員帳號註銷後 3 個月。</p>
              <p>7.2 <strong>交易記錄：</strong>依照稅法及商業會計法規定，保存至少 5 年。</p>
              <p>7.3 <strong>客服記錄：</strong>保存 2 年，以便處理爭議與改善服務。</p>
              <p>7.4 <strong>行銷資料：</strong>保存至您取消訂閱或要求刪除為止。</p>
              <p>7.5 <strong>技術日誌：</strong>保存 6 個月，用於系統維護與安全監控。</p>
              
              <p className="mt-6">資料保存期限屆滿後，本站將依照內部程序刪除或匿名化處理您的個人資料。</p>
            </div>
          </section>

          {/* Section 8 */}
          <section className="mb-12">
            <div className="flex items-center mb-6">
              <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center mr-4">
                <i className="ri-parent-line text-white text-xl"></i>
              </div>
              <h2 className="text-2xl font-bold text-gray-900">八、未成年人保護</h2>
            </div>
            <div className="pl-16 space-y-4 text-gray-700 leading-relaxed">
              <p>8.1 本站服務對象為年滿 18 歲之成年人。</p>
              <p>8.2 若您未滿 18 歲，請在法定代理人（如父母或監護人）同意下使用本站服務。</p>
              <p>8.3 若我們發現在未經法定代理人同意的情況下蒐集了未成年人的個人資料，將立即刪除該資料。</p>
              <p>8.4 法定代理人可以要求查閱、更正或刪除未成年人的個人資料。</p>
            </div>
          </section>

          {/* Section 9 */}
          <section className="mb-12">
            <div className="flex items-center mb-6">
              <div className="w-12 h-12 bg-gradient-to-br from-gray-500 to-slate-600 rounded-xl flex items-center justify-center mr-4">
                <i className="ri-edit-line text-white text-xl"></i>
              </div>
              <h2 className="text-2xl font-bold text-gray-900">九、隱私權政策修改</h2>
            </div>
            <div className="pl-16 space-y-4 text-gray-700 leading-relaxed">
              <p>9.1 本站保留隨時修改本隱私權政策之權利。</p>
              <p>9.2 修改後的政策將公布於本網頁，並註明最後更新日期。</p>
              <p>9.3 重大變更時，本站將透過網站公告、電子郵件或簡訊通知您。</p>
              <p>9.4 繼續使用本站服務即表示您同意接受修改後的隱私權政策。</p>
              <p>9.5 若您不同意修改後的政策，請停止使用本站服務並聯繫客服註銷帳號。</p>
            </div>
          </section>

          {/* Contact */}
          <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-2xl p-8 mt-12">
            <div className="flex items-start">
              <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-600 rounded-xl flex items-center justify-center mr-4 flex-shrink-0">
                <i className="ri-customer-service-2-line text-white text-xl"></i>
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">聯絡我們</h3>
                <p className="text-gray-700 mb-4">若您對本隱私權政策有任何疑問，或想行使您的個人資料權利，歡迎透過以下方式聯繫我們：</p>
                <div className="flex flex-wrap gap-4">
                  <a href="https://line.me/ti/p/@laigame" target="_blank" rel="noopener noreferrer" className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-xl hover:from-green-600 hover:to-emerald-700 transition-all shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer">
                    <i className="ri-line-fill text-xl mr-2"></i>
                    LINE 客服
                  </a>
                  <a href="https://www.facebook.com/laigame" target="_blank" rel="noopener noreferrer" className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl hover:from-blue-700 hover:to-indigo-700 transition-all shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer">
                    <i className="ri-facebook-fill text-xl mr-2"></i>
                    Facebook 粉專
                  </a>
                </div>
                <p className="text-sm text-gray-600 mt-4">客服時間：24 小時全年無休</p>
                <p className="text-sm text-gray-600 mt-2">我們將在收到您的請求後 15 個工作天內回覆</p>
              </div>
            </div>
          </div>

        </div>
      </div>

      <Footer />
    </div>
  );
}